import * as React from 'react';
import { Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { getUsersPaginated } from '../../Api';
import type AccountMainInfoDto from '../../models/dtos/AccountMainInfoDto';

export default function PushTestUserPicker({value,onChange,validationError,disabled}: {
    value:AccountMainInfoDto[]; onChange:(users:AccountMainInfoDto[])=>void; validationError?:string; disabled?:boolean;
}) {
    const [query,setQuery]=React.useState('');
    const [options,setOptions]=React.useState<AccountMainInfoDto[]>([]);
    const [loading,setLoading]=React.useState(false);
    const [error,setError]=React.useState('');
    const [retry,setRetry]=React.useState(0);
    React.useEffect(()=>{
        let live=true;
        setOptions([]);setError('');
        const term=query.trim();
        if(term.length<2&&!/^\d+$/.test(term)){setLoading(false);return;}
        setLoading(true);
        const timer=window.setTimeout(async()=>{
            try {
                const result=await getUsersPaginated(0,20,term);
                if(!live)return;
                if(!result)throw new Error('User search is unavailable. Please retry.');
                const seen=new Set<number>();
                setOptions(result.items.filter(user=>{
                    if(!Number.isSafeInteger(user.id)||user.id<=0||seen.has(user.id))return false;
                    seen.add(user.id);return true;
                }));
            } catch {if(live)setError('User search is unavailable. Please retry.');}
            finally {if(live)setLoading(false);}
        },400);
        return()=>{live=false;window.clearTimeout(timer);};
    },[query,retry]);
    const allOptions=[...value,...options.filter(option=>!value.some(user=>user.id===option.id))];
    return <Stack spacing={1} sx={{flex:1,minWidth:0}} data-push-field="testIds">
        <Autocomplete multiple disabled={disabled} options={allOptions} value={value} inputValue={query}
            onInputChange={(_,text,reason)=>{if(reason==='input'||reason==='clear')setQuery(text);}}
            onChange={(_,users)=>{onChange(users);setQuery('');}}
            filterOptions={users=>users} filterSelectedOptions
            isOptionEqualToValue={(left,right)=>left.id===right.id}
            getOptionLabel={user=>`${user.name||'Unnamed user'} · #${user.id}`}
            getOptionDisabled={()=>value.length>=20}
            loading={loading}
            noOptionsText={query.trim()?'No users found':'Search by name, email, or user ID'}
            renderOption={(props,user)=><Box component="li" {...props} key={user.id}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{minWidth:0}}>
                    <Avatar src={user.photoUrl} alt={user.name||'Unnamed user'} sx={{width:36,height:36}}/>
                    <Box sx={{minWidth:0}}>
                        <Typography variant="body2" fontWeight={500} sx={{overflowWrap:'anywhere'}}>{user.name||'Unnamed user'}</Typography>
                        <Typography variant="caption" color="text.secondary">#{user.id}{user.osType==='ios'?' · iOS':user.osType==='android'?' · Android':''}</Typography>
                    </Box>
                </Stack>
            </Box>}
            renderTags={(users,getTagProps)=>users.map((user,index)=><Chip {...getTagProps({index})} key={user.id}
                avatar={<Avatar src={user.photoUrl} alt={user.name||'Unnamed user'}/>}
                label={`${user.name||'Unnamed user'} · #${user.id}`} sx={{maxWidth:'100%'}}/>)}
            renderInput={params=><TextField {...params} label="Test users" placeholder={value.length?'Add another user':'Search by name, email, or user ID'}
                error={!!validationError||!!error}
                helperText={validationError??`${value.length}/20 selected. Choose users from search results; typing alone does not select a recipient.`}
                InputProps={{...params.InputProps,endAdornment:<>{loading&&<CircularProgress size={18}/>} {params.InputProps.endAdornment}</>}}/>}/>
        {error&&<Alert severity="warning" action={<Button color="inherit" onClick={()=>setRetry(count=>count+1)}>Retry</Button>}>{error}</Alert>}
    </Stack>;
}
